import type { BrowserSession, BrowserSessionListResponse } from "../../types/browser-session";
import { useApiQuery } from "../use-api-query";

/**
 * Fetches browser sessions created during a specific Argus agent session
 * (up to 50, newest first).
 *
 * Since ARGUS_VERSION 0.10.3 the gateway stamps every session-scoped
 * subprocess with `X-Argus-Created-By: agent:<pi_session_id>` (via the
 * per-session bash `spawnHook` and the internal BackendClient's
 * `mergedHeaders`). Filtering on that header-derived `created_by` value
 * yields exactly the browser sessions opened during this agent session —
 * no instance-wide over-fetching, no fragile client-side time windowing.
 *
 * Refetches every 5s so the artifacts panel tracks live changes as the
 * agent opens/closes browser sessions.
 */
export function useArgusAgentSessionBrowserSessions(
  agentSessionId: string | null,
): BrowserSession[] {
  const createdBy = agentSessionId ? `agent:${agentSessionId}` : "";
  const query = createdBy
    ? `/browser-sessions?limit=50&created_by=${encodeURIComponent(createdBy)}`
    : "";

  const { data } = useApiQuery<BrowserSessionListResponse>(query, {
    enabled: Boolean(createdBy),
    refetchInterval: 5000,
  });

  return data?.sessions ?? [];
}
